/**
 * PointsLine Component
 * 
 * Renders a single line segment between two 3D points using Three.js Line2
 * for enhanced line rendering with configurable width and color.
 * Used primarily for connecting measurement points in the 3D viewer.
 */

import React, { useEffect, useRef } from "react";
import { Line, Vector2, Vector3 } from "three";
import { extend } from "@react-three/fiber";
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib';

extend({ Line });

/**
 * Props for the PointsLine component
 */
interface PointsLineProps {
  /** Starting point of the line segment */
  start?: Vector3;
  /** Ending point of the line segment */
  end?: Vector3;
  /** Whether the line should be visible */
  visible?: boolean;
  /** Deprecated: Use lineColor instead */
  color?: string;
  /** Color of the line as a hexadecimal number (e.g., 0x00ff00 for green) */
  lineColor?: number;
}

const PointsLine: React.FC<PointsLineProps> = ({
  start,
  end,
  color = "green", // Deprecated, kept for backward compatibility
  lineColor = 0x00ff00,
  visible = true,
}: PointsLineProps) => {
  const lineRef = useRef<Line2>(new Line2());

  // Build points array from start and end positions
  const points: Vector3[] = [];
  if (start) {
    points.push(start);
  }
  if (end) {
    points.push(end);
  }

  // Determine visibility for individual points and line
  const firstPointVisible = points.length > 0 && visible;
  const secondPointVisible = points.length > 1 && visible;
  const firstPoint = points.length > 0 ? points[0] : new Vector3();
  const secondPoint = points.length > 1 ? points[1] : new Vector3();

  // Set up line geometry and material
  const geometry = new LineGeometry();
  const material = new LineMaterial({
    color: lineColor,
    linewidth: 5,
    resolution: new Vector2(window.innerWidth, window.innerHeight),
    depthTest: false,
  });

  useEffect(() => {
    // Only render line if we have at least 2 points
    if (points.length < 2) {
      return;
    }

    if (!lineRef.current) {
      return;
    }

    // Convert Vector3 points to flat number array for Line2 geometry
    const positions: number[] = [];
    points.forEach(point => {
      positions.push(point.x, point.y, point.z);
    });

    // Update line geometry with new positions
    geometry.setPositions(positions);
    lineRef.current.geometry = geometry;
    lineRef.current.material = material;
  }, [points, geometry, material]);

  return (
    <>
      {/* Render the connecting line between points */}
      <primitive 
        object={lineRef.current} 
        ref={lineRef} 
        visible={secondPointVisible} 
        renderOrder={2} 
      />
      
      {/* Render start point as a small sphere */}
      <mesh position={firstPoint} visible={firstPointVisible} renderOrder={3}>
        <sphereGeometry args={[0.05, 36, 36]} />
        <meshBasicMaterial color={color} depthTest={false} />
      </mesh>
      
      {/* Render end point as a small sphere */}
      <mesh position={secondPoint} visible={secondPointVisible} renderOrder={3}>
        <sphereGeometry args={[0.05, 36, 36]} />
        <meshBasicMaterial color={color} depthTest={false} />
      </mesh>
    </>
  );
};

export default PointsLine;